export default function HouseholdChoresPost() {
  return (
    <>
      <p className="lead">
        My girls have been earning an allowance for years. And during that time, it
        worked the way most allowances do: a set amount landed in their pockets
        every week, whether or not a single thing got done around the house.
        Dishes, laundry, the stuff we'd agreed they'd handle. None of it was
        actually tied to the money. The money just showed up.
      </p>
      <p>This week I decided to change that.</p>
      <p>
        What really pushed me was the move. We landed in a new place about a
        month ago, and the girls still haven't fully unpacked. The boxes are
        empty, sure, but there are piles of clothes sitting right next to empty
        dresser drawers, and a TV parked in the middle of a bedroom floor. I
        wanted a way to motivate some action without turning into a nag about it.
      </p>
      <p>
        Not because I wanted to turn my house into a chore factory, but because I
        wanted the deal to be fair. There should be some incentive to do the
        things you're already expected to do. I wanted it visible, something the
        whole house could see, so there was a little merit to it, a little
        healthy competition between sisters, and some accountability on my end
        too. I have my own responsibilities in this house, and I liked the idea
        of the kids being able to look at a board and see whether Dad held up his
        side of the week.
      </p>

      <h2>Why the retro board</h2>
      <p>
        Here's what made me laugh: I build a retrospective and polling tool
        called Retroboard. It's not what pays the bills, it's a side project I
        keep tinkering on. But its whole reason for existing is spotting patterns
        over time: what's working, what keeps slipping. Both of my girls have
        phones, so both of them can already pull up a board. One evening it hit
        me. I made this thing. Why am I not pointing it at my own household?
      </p>
      <p>
        What I wanted was simple. Every week I want to see which responsibilities
        got handled and which ones got skipped. Over time I want to capture the
        pattern of what consistently gets done and what consistently doesn't,
        with the kids able to weigh in themselves.
      </p>

      <h2>The flip that makes it work</h2>
      <p>
        The most important decision I made was about framing, and it's the
        opposite of how most chore charts work.
      </p>
      <p>
        It's not "do your chores and you'll earn an allowance." It's this:{" "}
        <strong>you already have your allowance, and now you keep it.</strong>{" "}
        The money is theirs at the start of the week. Doing what's expected is
        how they protect it. Fall short and some of it comes off. And if they
        want more, a weekend with friends or a shopping trip, there's a pool of
        bonus tasks they can pick up to add to it.
      </p>
      <p>
        Same dollars, completely different feeling. Protecting something you
        already have should carry more weight than chasing something you don't.
      </p>

      <h2>How the board is laid out</h2>
      <p>
        Each kid gets her own column. At a glance you know exactly what each girl
        is responsible for that week. There's a column for me too, the things
        Dad is on the hook for, because accountability that only points one
        direction isn't really accountability.
      </p>
      <p>
        Then there's a bonus column, a stack of extra tasks the kids can drag
        into their own column and take care of if they want to boost their week.
      </p>
      <p>
        The column headers do double duty. Each one is named for the kid and
        lists how much she gets per week, an amount tied to her age. The header
        itself is the scoreboard.
      </p>

      <h2>Getting their buy-in</h2>
      <p>
        I didn't build this and hand it down. My youngest sat at the computer
        with me while we came up with bonus cards, and she immediately started
        stacking my column: play video games with her, a movie night with
        popcorn, a Saturday trip to our regular breakfast spot. Apparently Dad
        has responsibilities too, and the ones she cared about were really just
        about showing up for her. My oldest read through her own column and
        signed off that it was fair. That was the moment it stopped being my idea
        and started being something they had a hand in.
      </p>

      <h2>The cards, the heart, and the polls</h2>
      <p>
        Each responsibility lives on a card, and the dollar amount rides right on
        the card. If I check in during the week and something hasn't been done, I
        mark it. Because the amount is written there, everyone knows exactly
        what's coming off next payday. Bonus cards that got dragged over and
        finished count the other way, as extra money at payout.
      </p>
      <p>
        I use the heart as a parent check-in. At the start of the week I heart the
        items I expect to see done. If at any point I find evidence something
        didn't happen, I un-heart it. That un-heart is the cue: this one is going
        to cost you.
      </p>
      <p>
        I lean on polls for the nuance. It's not just done or missed, there's
        room for "exceeded expectations," set up as poll options on the card. The
        nice part is what happens over time. When I roll the board over to a new
        week, the poll values aggregate. Over a month or two that should let me look back and
        see things like one daughter being really good at a certain thing while
        another keeps struggling with it. That's the pattern-spotting I built the
        tool for in the first place. I just never pictured the subject being my
        own kids.
      </p>

      <h2>Payout day</h2>
      <p>
        The math is simple. At the end of the week we sit down together and go
        column by column. Un-hearted responsibilities come off the allowance, and
        completed bonus items get added on. Right now I haven't loaded up enough
        required tasks for anyone to go negative in a week, but they can
        definitely get zeroed out. That floor and ceiling feel about right for
        where the girls are.
      </p>

      <h2>Resetting is almost nothing</h2>
      <p>
        I worried the weekly reset would be a chore of its own. The way it's built, it shouldn't be.
        Everything I want to carry forward is pinned, so when I clone the board,
        every pinned card lands in the next week with its column and text intact,
        and the polls reset clean so each week's poll is its own count. After
        that I just drag a few bonus cards back into the bonus column and I'm
        done. I put a star in the text of the bonus cards so I can tell them apart
        from the responsibility cards at a glance. That's the whole ritual.
      </p>
      <p>
        If you would rather not build the board from scratch, I saved this setup
        as a <a href="/templates/household-chores">Household Chores template</a>{" "}
        you can start from, and the rest of the{" "}
        <a href="/templates">Retroboard templates</a> live here too.
      </p>

      <h2>What it's really about</h2>
      <p>
        Under all the mechanics, this was never really about squeezing more chores
        out of my kids. It's about making the invisible visible, for them and for
        me. They can see what they own, they can see each other, and they can see
        that Dad's name is up there with tasks next to it too. The allowance is
        just the language we're using to have a real, ongoing conversation about
        pulling our weight as a family.
      </p>
    </>
  )
}
